import React from 'react';
import clsx from 'clsx';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { storyFrameOf } from 'fym-shared/src/music24/story/storyFrames';
import {
  LYRIC_VIDEO_CANVAS,
  LYRIC_VIDEO_COLUMN,
  LYRIC_VIDEO_LAYOUT,
  lyricVideoLyricPx,
  lyricVideoShowsCover,
  lyricVideoTextLayout,
  type LyricVideoStyle,
} from 'fym-shared/src/music24/story/lyricVideo';
import { TypedMemo } from '../../../../common/types';
import { FrameLyric, FrameSong } from './assetFrameVars';
import { frameThemeClass } from './frameClasses';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the frames; each frame uses its own slice of it */
import styles from './AssetFrame.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricVideoFrameProps {
  themeId: ReleaseThemeId;
  /** The style of the video (design y3): the cover card, the blurred cover ground, or type only. */
  style: LyricVideoStyle;
  song: FrameSong;
  lyric: FrameLyric;
  vars: React.CSSProperties;
  className?: string;
}

/** A length of the 1920 px canvas as a share of the frame width (the frame is a size container). */
const cqw = (px: number): string => `${((px / LYRIC_VIDEO_CANVAS.width) * 100).toFixed(3)}cqw`;

/** The text sits over the ground, the blurred cover and its scrim (z-index 1). */
const TEXT_LAYER = 2;

/**
 * A still of the lyric video in its lyric state (designs `asset-redesign/y` and `y3`), drawn from
 * the boxes that the renderer uses (`lyricVideoTextLayout`, `LYRIC_VIDEO_COLUMN` of fym-shared):
 * the lyric column on the right (the line before dim, the sung line, the next line dim) in the
 * artist's own case. Cover card: the cover in its theme frame on the left with the title and the
 * artist under it. Cover ground: the blurred cover under a scrim, the title and the artist larger
 * at the top left, no cover card. Type only: the same text on the theme ground, no cover.
 */
const LyricVideoFrameComponent: React.FC<LyricVideoFrameProps> = ({ themeId, style, song, lyric, vars, className }) => {
  const layout = lyricVideoTextLayout(style).lyrics;
  const withCover = lyricVideoShowsCover(style);
  const { cover } = LYRIC_VIDEO_LAYOUT.lyrics;
  const lyricPx = lyricVideoLyricPx(style, storyFrameOf(themeId).factors.display);
  const dim: React.CSSProperties = {
    fontFamily: 'var(--df)',
    fontWeight: 'var(--dw)' as React.CSSProperties['fontWeight'],
    color: 'color-mix(in srgb, var(--t) 45%, transparent)',
    fontSize: cqw(lyricPx * 0.62),
    lineHeight: 1.1,
  };
  return (
    <div
      className={clsx(styles.fv, frameThemeClass(themeId), className)}
      style={vars}
      aria-hidden="true"
      data-frame="lyricVideo"
      data-layout="wide"
      data-style={style}
    >
      <i className={styles.g} />
      {style === 'ground' && (
        <>
          <i className={styles.blur} data-testid="lyric-video-frame-blur" />
          <i className={styles.scrim} />
        </>
      )}
      {withCover && (
        <i
          className={styles.maCover}
          data-testid="lyric-video-frame-cover"
          style={{ left: cqw(cover.x), top: cqw(cover.y), width: cqw(cover.size), zIndex: TEXT_LAYER }}
        />
      )}
      <span
        style={{
          zIndex: TEXT_LAYER,
          left: cqw(layout.textX),
          top: cqw(layout.textY),
          width: cqw(layout.textW),
          fontFamily: 'var(--df)',
          fontWeight: 'var(--dw)' as React.CSSProperties['fontWeight'],
          textTransform: 'var(--dc)' as React.CSSProperties['textTransform'],
          fontSize: cqw(layout.titlePx),
          color: 'var(--t)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {song.title}
      </span>
      <span
        style={{
          zIndex: TEXT_LAYER,
          left: cqw(layout.textX),
          top: cqw(layout.textY + layout.titlePx * 1.3),
          fontFamily: 'var(--tf)',
          fontSize: cqw(layout.artistPx),
          color: 'var(--t)',
          whiteSpace: 'nowrap',
        }}
      >
        {song.artist}
      </span>
      <span
        style={{
          zIndex: TEXT_LAYER,
          left: cqw(LYRIC_VIDEO_COLUMN.x),
          top: cqw(LYRIC_VIDEO_COLUMN.top),
          width: cqw(LYRIC_VIDEO_COLUMN.w),
          height: cqw(LYRIC_VIDEO_COLUMN.bottom - LYRIC_VIDEO_COLUMN.top),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: cqw(LYRIC_VIDEO_COLUMN.gap * 0.5),
          overflow: 'hidden',
        }}
      >
        {lyric.prev !== '' && <span style={dim}>{lyric.prev}</span>}
        <span
          data-testid="lyric-video-frame-sung"
          style={{
            fontFamily: 'var(--df)',
            fontWeight: 'var(--dw)' as React.CSSProperties['fontWeight'],
            fontSize: cqw(lyricPx),
            lineHeight: 1.05,
            color: 'var(--t)',
          }}
        >
          {lyric.current}
        </span>
        {lyric.next !== '' && <span style={dim}>{lyric.next}</span>}
      </span>
    </div>
  );
};

export const LyricVideoFrame = TypedMemo(LyricVideoFrameComponent);
